import React from "react";
import {
  AppBar,
  Toolbar,
  Stack,
  Link,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import { User } from "@auth0/auth0-react";
import Notifications from "@components/Notifications/Notifications";
import UserMenu from "./UserMenu";
import { APP_BAR_HEIGHT } from "../../constants";
import { useWebApiV1GetUserFeatureSwitchesQuery } from "@providers/hop-ord-server/api";
import { Feature, Role } from "@enums";
import { useAppSelector } from "@hooks";
import { useCheckUserRole } from "@utils";
import { useLocation } from "react-router-dom";

interface TopAppBarProps {
  user?: User;
  accessToken?: boolean;
}

interface MainLayoutProps extends TopAppBarProps {
  children: React.ReactNode;
}

const TopAppBar = ({ user, accessToken }: TopAppBarProps) => {
  const logoURL = new URL("@assets/header_logo.png", import.meta.url).href;
  const { data: featureSwitchData } = useWebApiV1GetUserFeatureSwitchesQuery();
  const isRo = useCheckUserRole(Role.RO);
  const hasDeploymentAccess = useCheckUserRole(Role.AUTOMATION_DEPLOYMENT);

  const lumonusAiUrl = process.env.VITE_LUMONUS_AI_URL || "";

  // Safely construct URL with error handling
  let url = "";
  let isValidUrl = false;
  try {
    if (lumonusAiUrl) {
      url = new URL("/sso/silent", lumonusAiUrl).href;
      isValidUrl = true;
    }
  } catch (error) {
    console.warn("Invalid VITE_LUMONUS_AI_URL provided:", lumonusAiUrl, error);
    isValidUrl = false;
  }

  const showLinkToLumonusAi =
    isValidUrl &&
    featureSwitchData?.find(
      (feature) => feature.name === Feature.SHOW_LINK_TO_LUMONUS_AI,
    ) &&
    isRo;

  const roles = useAppSelector((state) => state?.auth?.roles) ?? [];
  const hasBothClinicalRoles =
    roles.includes(Role.DOSIMETRIST) && roles.includes(Role.RO);
  // Need to attach the JWT token to the URL in the authorization header
  const token = useAppSelector((state) => state?.auth?.token);
  const urlWithToken = isValidUrl ? `${url}?token=${token}` : "";
  const envType = process.env.VITE_ENVIRONMENT_TYPE ?? "unknown";

  // Only show the metrics link if the user is an admin
  const isAdmin = useCheckUserRole(Role.ADMIN);

  const isOnMetrics = useLocation().pathname === "/metrics";
  const isOnPlanning = useLocation().pathname === "/commandcenter";
  const isOnDeploymentPage = useLocation().pathname === "/deploymentdashboard";

  return (
    <AppBar
      position="fixed"
      color="primary"
      id="app-bar"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        height: APP_BAR_HEIGHT,
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          paddingX: 2,
          height: 1,
          justifyContent: "space-between",
        }}
        disableGutters
      >
        <Stack direction="row" gap={10} sx={{ height: 1, alignItems: "end" }}>
          <Link href="/">
            <img src={logoURL} alt="lumonus logo" style={{ height: 44 }} />
          </Link>
          <Stack direction="row" gap={4}>
            {showLinkToLumonusAi && (
              <Stack
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  paddingBottom: 1.75,
                }}
              >
                <Link
                  href={urlWithToken}
                  underline="none"
                  color="inherit"
                  variant="subtitle2"
                >
                  APPOINTMENTS
                </Link>
              </Stack>
            )}
            <Stack
              sx={{
                alignItems: "center",
                justifyContent: "center",
                borderBottom: isOnPlanning ? "2px solid" : "none",
                paddingBottom: 1.75,
              }}
            >
              <Link
                href="/"
                underline="none"
                color="inherit"
                variant="subtitle2"
              >
                PLANNING
              </Link>
            </Stack>
            {isAdmin && (
              <Stack
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  borderBottom: isOnMetrics ? "2px solid" : "none",
                  paddingBottom: 1.75,
                }}
              >
                <Link
                  href="/metrics"
                  underline="none"
                  color="inherit"
                  variant="subtitle2"
                >
                  METRICS
                </Link>
              </Stack>
            )}
            {hasDeploymentAccess && (
              <Stack
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  borderBottom: isOnDeploymentPage ? "2px solid" : "none",
                  paddingBottom: 1.75,
                }}
              >
                <Link
                  href="/deploymentdashboard"
                  underline="none"
                  color="inherit"
                  variant="subtitle2"
                >
                  DEPLOYMENT
                </Link>
              </Stack>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          {hasBothClinicalRoles && (
            <Typography
              variant="body1"
              color="primary.contrastText"
              sx={{ marginRight: 1 }}
            >
              {isRo ? "Physician" : "Dosimetrist"}
            </Typography>
          )}
          {!["prod", "demo"].includes(
            process.env.VITE_ENVIRONMENT_TYPE || "",
          ) && (
            <Chip
              color="error"
              label={envType}
              sx={{
                fontSize: 24,
                boxShadow: "2px 2px 8px 4px #f00b, -2px -2px 8px 4px #05f",
                fontWeight: 700,
                background:
                  envType === "dev"
                    ? "linear-gradient(to bottom right, #2c90fc 0%, #b8fd33 33%, #fec837 66%, #fd1892 100%)"
                    : "",
              }}
            />
          )}
          {user && accessToken && <Notifications />}
          {user && <UserMenu user={user} />}
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

const Layout = ({ user, accessToken, children }: MainLayoutProps) => {
  return (
    <>
      <TopAppBar user={user} accessToken={accessToken} />
      {/* Main Content */}
      <Stack
        flex={1}
        height={`calc(100vh - ${APP_BAR_HEIGHT}px)`}
        marginTop={`${APP_BAR_HEIGHT}px`}
        direction="row"
      >
        <Stack flex={1} width={1} minWidth={0}>
          <Box id="main-banner" />
          {children}
        </Stack>
        <Box id="layout-right-bar" />
      </Stack>
    </>
  );
};

export default Layout;
